// main.js - Application entry point

// Import global styles - this imports all component styles via @import in global.css
import './styles/global.css';

// Clean URLs are now handled directly by the components

// Add Google Material Icons - load full Material Symbols Sharp font
const iconLink = document.createElement('link');
iconLink.rel = 'stylesheet';
iconLink.href = 'https://fonts.googleapis.com/css2?family=Material+Symbols+Sharp:opsz,wght,FILL,GRAD@20..48,100..700,0..1,-50..200';
document.head.appendChild(iconLink);

// Import components
import Header from './components/Header/Header.js';
import Explorer from './components/Explorer/Explorer.js';
import Tabs from './components/Tabs/Tabs.js';
import Terminal from './components/Terminal/Terminal.js';
import ResizeHandle from './components/ResizeHandle/ResizeHandle.js';
import README from './components/Newsletter/README.js';
import TeamPage from './components/About/TeamPage.js';
import TheBizInfo from './components/Content/TheBizInfo.js';
import ComponentsForSale from './components/Content/ComponentsForSale.jsx';
import DoItSh from './components/Content/DoItSh.js';
import GreenCheckWrapper from './components/apps/greencheck/GreenCheckWrapper.js';
import AppsWrapper from './components/apps/AppsWrapper.js';
import AppContent from './components/apps/AppContent.js';
import OlogiesWrapper from './components/ologies/OlogiesWrapper.js';
import OlogyContent from './components/ologies/OlogyContent.js';
import AdminPanel from './components/Admin/AdminPanel.js';
import SplashScreen from './components/SplashScreen/SplashScreen.js';
import { initMobile } from './utils/MobileManager.js';
import router from './utils/Router.js';

/**
 * Initialize the application
 */
function initApp() {
  // Add CSS class to body for component selector support
  document.body.classList.add('app-container');
  
  // Create a splash screen container
  const splashContainer = document.createElement('div');
  splashContainer.id = 'splash-container';
  document.body.appendChild(splashContainer);
  
  // Initialize and show splash screen
  const splashScreen = new SplashScreen(splashContainer);
  splashScreen.init();
  
  // Hide main content initially
  document.getElementById('app-container').style.visibility = 'hidden';
  
  // Listen for splash screen completion
  splashContainer.addEventListener('splashscreen:complete', async () => {
    // Show main content
    document.getElementById('app-container').style.visibility = 'visible';
    
    // Initialize main application
    await initMainApp();
  });
}

/**
 * Initialize the main application after splash screen completes
 */
async function initMainApp() {
  // Get container elements
  const headerContainer = document.getElementById('slim-header');
  const explorerContainer = document.getElementById('left-nav-drawer');
  const tabsContainer = document.getElementById('tab-header');
  const contentContainer = document.getElementById('content-area');
  const terminalContainer = document.getElementById('terminal-drawer');
  const explorerResizeHandle = document.getElementById('resize-handle');
  const terminalResizeHandle = document.getElementById('terminal-resize-handle');
  
  // Initialize navigation buttons
  const navArrowLeft = document.getElementById('nav-arrow-left');
  const navArrowRight = document.getElementById('nav-arrow-right');
  
  // Initialize navigation history
  const navHistory = {
    history: ['welcome'], // Start with welcome tab
    currentIndex: 0,
    
    // Add a tab to history
    addTab(tabId) {
      // Don't add duplicate entries of the active tab
      if (this.currentIndex >= 0 && this.history[this.currentIndex] === tabId) {
        return;
      }
      
      // If we're not at the end of the history, truncate
      if (this.currentIndex < this.history.length - 1) {
        this.history = this.history.slice(0, this.currentIndex + 1);
      }
      
      this.history.push(tabId);
      this.currentIndex = this.history.length - 1;
      
      this.updateNavButtons();
    },
    
    // Navigate back
    goBack() {
      if (this.currentIndex > 0) {
        this.currentIndex--;
        const tabId = this.history[this.currentIndex]; 
        this.updateNavButtons();
        return tabId;
      }
      return null;
    },
    
    // Navigate forward
    goForward() {
      if (this.currentIndex < this.history.length - 1) {
        this.currentIndex++;
        const tabId = this.history[this.currentIndex];
        this.updateNavButtons();
        return tabId;
      }
      return null;
    },
    
    // Update navigation buttons state
    updateNavButtons() {
      // Disable back button if at beginning of history
      if (this.currentIndex <= 0) {
        navArrowLeft.classList.add('disabled');
      } else {
        navArrowLeft.classList.remove('disabled');
      }
      
      // Disable forward button if at end of history
      if (this.currentIndex >= this.history.length - 1) {
        navArrowRight.classList.add('disabled');
      } else {
        navArrowRight.classList.remove('disabled');
      }
    }
  };
  
  // Initialize components
  const header = new Header(headerContainer);
  const explorer = new Explorer(explorerContainer);
  const tabs = new Tabs(tabsContainer, contentContainer);
  const terminal = new Terminal(terminalContainer, { visible: true });
  
  // Make terminal globally accessible
  window.terminal = terminal;
  
  // Initialize resize handles
  const navResizer = new ResizeHandle(
    explorerResizeHandle, 
    explorerContainer, 
    { 
      direction: 'horizontal',
      minSize: 150,
      maxSize: 500,
      initialSize: 250,
      storageName: 'app-explorer-width'
    }
  );
  
  const terminalResizer = new ResizeHandle(
    terminalResizeHandle, 
    terminalContainer, 
    { 
      direction: 'vertical',
      minSize: 100,
      maxSize: 600,
      initialSize: 200,
      storageName: 'app-terminal-height'
    }
  );
  
  // Render all components
  header.render();
  explorer.render();
  await tabs.render(); // Await the async tabs render
  terminal.render();
  navResizer.render();
  terminalResizer.render();
  
  // Ensure terminal starts open at max height
  terminal.open();
  
  // Initialize nav buttons state
  navHistory.updateNavButtons();
  
  // Initialize router with tabs component in history mode for clean URLs
  router.init(tabs);
  
  // Make router available globally for button handlers and debugging
  window.router = router;
  
  // Add your routes
  router.addRoutes({
    '/welcome': {
      title: 'Welcome',
      load: () => tabs.activateTab('welcome')
    },
    '/about': {
      title: 'About Us',
      load: () => {
        // Open the about-us content
        const openEvent = new CustomEvent('file:open', { 
          detail: { filepath: 'content/about-us.txt' },
          bubbles: true 
        });
        document.dispatchEvent(openEvent);
      }
    },
    '/team': {
      title: 'Our Team',
      load: () => {
        // Show team landing page or first team member
        const openEvent = new CustomEvent('file:open', { 
          detail: { filepath: 'about/the-team.md' },
          bubbles: true 
        });
        document.dispatchEvent(openEvent);
      }
    },
    '/projects': {
      title: 'Projects',
      load: () => {
        // Show projects landing page
        const openEvent = new CustomEvent('file:open', { 
          detail: { filepath: 'apps/VIBED_CREATIONS.apps' },
          bubbles: true 
        });
        document.dispatchEvent(openEvent);
      }
    },
    '/apps/VIBED_CREATIONS.apps': {
      title: 'VIBED CREATIONS',
      load: () => {
        // Open the apps aggregation page
        const openEvent = new CustomEvent('file:open', { 
          detail: { filepath: 'apps/VIBED_CREATIONS.apps' },
          bubbles: true 
        });
        document.dispatchEvent(openEvent);
      }
    },
    '/newsletter': {
      title: 'README.sub',
      load: () => {
        // Dispatch a file:open event for were_not_sorry/README.sub
        const openEvent = new CustomEvent('file:open', { 
          detail: { filepath: 'were_not_sorry/README.sub' },
          bubbles: true 
        });
        document.dispatchEvent(openEvent);
      }
    },
    '/adminvibes': {
      title: 'Admin Vibes',
      load: () => {
        // Open the admin panel
        const openEvent = new CustomEvent('file:open', { 
          detail: { filepath: 'admin/admin-panel.html' },
          bubbles: true 
        });
        document.dispatchEvent(openEvent);
      }
    }
  });
  
  // Event listeners for navigation interactions
  document.addEventListener('file:open', (event) => {
    const { filepath, fromRouter } = event.detail;
    let tabActivated = false;
    
    // Special handling for ologies aggregation page
    if (filepath === 'ologies/EVERYTHING.json') {
      const tabId = 'ologies/EVERYTHING.json';
      const tabTitle = 'EVERYTHING.json';
      
      if (!tabs.hasTab(tabId)) {
        const container = document.createElement('div');
        const ologiesPage = new OlogiesWrapper(container);
        ologiesPage.render();
        tabs.addTab(tabId, tabTitle, container, { active: true });
        tabActivated = true;
      } else {
        tabs.activateTab(tabId);
        tabActivated = true;
      }
      
      // Add to history if tab was activated
      if (tabActivated) {
        navHistory.addTab(tabId);
        
        // Only update URL if this wasn't triggered by router navigation
        // Check if the current URL already matches to prevent circular navigation
        if (!fromRouter) {
          const currentPath = router.getCurrentPath();
          const expectedPath = router.normalizePath('/file/ologies/EVERYTHING.json');
          if (currentPath !== expectedPath && router && typeof router.navigateToFile === 'function') {
          router.navigateToFile('ologies/EVERYTHING.json');
          }
        }
      }
      return;
    }
    
    // Special handling for apps aggregation page
    if (filepath === 'apps/VIBED_CREATIONS.apps') {
      const tabId = 'apps/VIBED_CREATIONS.apps';
      const tabTitle = 'VIBED_CREATIONS.apps';
      
      if (!tabs.hasTab(tabId)) {
        const container = document.createElement('div');
        const appsPage = new AppsWrapper(container);
        appsPage.render();
        tabs.addTab(tabId, tabTitle, container, { active: true });
        tabActivated = true;
      } else {
        tabs.activateTab(tabId);
        tabActivated = true;
      }
      
      // Add to history if tab was activated
      if (tabActivated) {
        navHistory.addTab(tabId);
        
        // Only update URL if this wasn't triggered by router navigation
        // Check if the current URL already matches to prevent circular navigation
        if (!fromRouter) {
          const currentPath = router.getCurrentPath();
          const expectedPath = router.normalizePath('/file/apps/VIBED_CREATIONS.apps');
          if (currentPath !== expectedPath && router && typeof router.navigateToFile === 'function') {
            router.navigateToFile('apps/VIBED_CREATIONS.apps');
          }
        }
      }
      return;
    }
    
    // Special handling for individual ology files
    if (filepath.startsWith('ologies/') && filepath !== 'ologies/EVERYTHING.json') {
      const slug = filepath.replace('ologies/', '');
      const tabId = filepath;
      const tabTitle = event.detail.title || slug;
      
      if (!tabs.hasTab(tabId)) {
        const container = document.createElement('div');
        const ologyContent = new OlogyContent(container, slug);
        ologyContent.render();
        tabs.addTab(tabId, tabTitle, container, { active: true });
        tabActivated = true;
      } else {
        tabs.activateTab(tabId);
        tabActivated = true;
      }
      
      // Add to history if tab was activated
      if (tabActivated) {
        navHistory.addTab(tabId);
        
        // Only update URL if this wasn't triggered by router navigation
        // Check if the current URL already matches to prevent circular navigation
        if (!fromRouter) {
          const currentPath = router.getCurrentPath();
          const expectedPath = router.normalizePath(`/ologies/${slug}`);
          if (currentPath !== expectedPath && router && typeof router.navigateToFile === 'function') {
            router.navigateToFile(filepath);
          }
        }
      }
      return;
    }
    
    // Special handling for individual app files
    if (filepath.startsWith('apps/') && filepath !== 'apps/VIBED_CREATIONS.apps' && !filepath.includes('.jsx') && !filepath.includes('.app')) {
      const slug = filepath.replace('apps/', '');
      const tabId = filepath;
      const tabTitle = event.detail.title || slug;
      
      if (!tabs.hasTab(tabId)) {
        const container = document.createElement('div');
        const appContent = new AppContent(container, slug);
        appContent.render();
        tabs.addTab(tabId, tabTitle, container, { active: true });
        tabActivated = true;
      } else {
        tabs.activateTab(tabId);
        tabActivated = true;
      }
      
      // Add to history if tab was activated
      if (tabActivated) {
        navHistory.addTab(tabId);
        
        // Only update URL if this wasn't triggered by router navigation
        // Check if the current URL already matches to prevent circular navigation
        if (!fromRouter) {
          const currentPath = router.getCurrentPath();
          const expectedPath = router.normalizePath(`/apps/${slug}`);
          if (currentPath !== expectedPath && router && typeof router.navigateToFile === 'function') {
          router.navigateToFile(filepath);
          }
        }
      }
      return;
    }
    
    // Extract filename for tab title
    const filename = filepath.split('/').pop();
    
    // Special handling for the team page
    if (filepath === 'about/the-team.md') {
      if (!tabs.hasTab(filepath)) {
        // Create container for team page component
        const container = document.createElement('div');
        
        // Initialize team page component
        const teamPage = new TeamPage(container);
        teamPage.render();
        
        // Add as tab - use the exact filename as the title
        tabs.addTab(filepath, filename, container, { active: true });
        tabActivated = true;
      } else {
        // If tab already exists, just activate it
        tabs.activateTab(filepath);
        tabActivated = true;
      }
    } else if (filepath === 'about/the-biz.info') {
      if (!tabs.hasTab(filepath)) {
        const container = document.createElement('div');
        const bizInfoPage = new TheBizInfo(container);
        bizInfoPage.render();
        tabs.addTab(filepath, filename, container, { active: true });
        tabActivated = true;
      } else {
        tabs.activateTab(filepath);
        tabActivated = true;
      }
    } else if (filepath === 'were_not_sorry/README.sub') {
      const tabId = filepath;
      const tabTitle = filename;

      if (!tabs.hasTab(tabId)) {
        const container = document.createElement('div');
        const newsletterComponent = new README(container);
        newsletterComponent.render();
        tabs.addTab(tabId, tabTitle, container, { active: true });
        tabActivated = true;
      } else {
        tabs.activateTab(tabId);
        tabActivated = true;
      }
    } else if (filepath === 'dm_us/do-it.sh') {
      if (!tabs.hasTab(filepath)) {
        const container = document.createElement('div');
        const doItShPage = new DoItSh(container);
        doItShPage.render();
        tabs.addTab(filepath, filename, container, { active: true });
        tabActivated = true;
      } else {
        tabs.activateTab(filepath);
        tabActivated = true;
      }
    } else if (filepath === 'apps/components_4_$ale.jsx') {
      if (!tabs.hasTab(filepath)) {
        const container = document.createElement('div');
        const componentsPage = new ComponentsForSale(container);
        componentsPage.render();
        tabs.addTab(filepath, filename, container, { active: true });
        tabActivated = true;
      } else {
        tabs.activateTab(filepath);
        tabActivated = true;
      }
    } else if (filepath === 'admin/admin-panel.html') {
      if (!tabs.hasTab(filepath)) {
        const container = document.createElement('div');
        const adminPanel = new AdminPanel(container);
        adminPanel.render();
        tabs.addTab(filepath, 'Admin Panel', container, { active: true });
        tabActivated = true;
      } else {
        tabs.activateTab(filepath);
        tabActivated = true;
      }
    } else {
      // Only add tab if it doesn't already exist
      if (!tabs.hasTab(filepath)) {
        // Fetch content asynchronously (in a real app, we'd get real content)
        const sampleContent = `<h2>${filename}</h2><p>Content for ${filepath}</p>`;
        tabs.addTab(filepath, filename, sampleContent, { active: true });
        tabActivated = true;
      } else {
        // If tab already exists, just activate it
        tabs.activateTab(filepath);
        tabActivated = true;
      }
    }
    
    // Only add to history if a tab was actually activated
    if (tabActivated) {
      navHistory.addTab(filepath);
      
      // Only update URL if this wasn't triggered by router navigation
      // Check if the current URL already matches to prevent circular navigation
      if (!fromRouter) {
        const currentPath = router.getCurrentPath();
        const expectedPath = router.normalizePath(`/file/${filepath}`);
        if (currentPath !== expectedPath && router && typeof router.navigateToFile === 'function') {
        router.navigateToFile(filepath);
        }
      }
    }
  });
  
  // Implement Explorer toggle
  document.getElementById('nav-drawer-toggle').addEventListener('click', () => {
    explorer.toggle();
  });
  
  // Implement Terminal toggle
  document.getElementById('terminal-toggle').addEventListener('click', () => {
    terminal.toggle();
  });
  
  // Implement back navigation
  navArrowLeft.addEventListener('click', () => {
    if (navArrowLeft.classList.contains('disabled')) return;
    
    const tabId = navHistory.goBack();
    if (tabId) {
      if (tabs.hasTab(tabId)) {
        tabs.activateTab(tabId);
      } else {
        // If tab was closed, try to reopen it
        const filename = tabId.split('/').pop();
        tabs.addTab(tabId, filename, `<h2>${filename}</h2><p>Content for ${tabId}</p>`, { active: true });
      }
    }
  });
  
  // Implement forward navigation
  navArrowRight.addEventListener('click', () => {
    if (navArrowRight.classList.contains('disabled')) return;
    
    const tabId = navHistory.goForward();
    if (tabId) {
      if (tabs.hasTab(tabId)) {
        tabs.activateTab(tabId);
      } else {
        // If tab was closed, try to reopen it
        const filename = tabId.split('/').pop();
        tabs.addTab(tabId, filename, `<h2>${filename}</h2><p>Content for ${tabId}</p>`, { active: true });
      }
    }
  });
  
  // Link Terminal file commands to tab system
  document.addEventListener('file:request-open', (event) => {
    const { filepath } = event.detail;
    
    // Create custom event to reuse the file:open handler
    const openEvent = new CustomEvent('file:open', { 
      detail: { filepath },
      bubbles: true 
    });
    document.dispatchEvent(openEvent);
  });
  
  // Initialize all mobile functionality
  initMobile();
  
  // Make sure toggle buttons match component states
  const terminalToggle = document.getElementById('terminal-toggle');
  if (terminalToggle && terminal.isVisible) {
    terminalToggle.classList.add('active');
  }
  
  // Better handling of nav toggle button state based on screen size
  const navToggle = document.getElementById('nav-drawer-toggle');
  const isMobile = window.matchMedia('(max-width: 768px)').matches;
  
  if (navToggle) {
    if (isMobile) {
      // On mobile, drawer starts closed so button should not be active
      navToggle.classList.remove('active');
      // Also ensure the drawer is actually closed
      const leftNavDrawer = document.getElementById('left-nav-drawer');
      if (leftNavDrawer) {
        leftNavDrawer.classList.remove('open');
        // If using explorer component
        if (explorer) {
          explorer.isVisible = false;
        }
      }
    } else {
      // On desktop, drawer starts open so button should be active
      navToggle.classList.add('active');
    }
  }
  
  console.log('Component-based application initialized');
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', initApp); 